/* /app/frontend/src/hooks/exchangeConfidence.js — iter-365
 *
 * Dynamic Exchange Confidence — a fully-explainable 0–100 score
 * that reflects how likely a deal is to reach Exchange.
 *
 * ── Design brief ─────────────────────────────────────────────
 *   1. Transaction Progress    40%  completed applicable milestones
 *   2. Risk & Blockers         30%  active blockers, overdue targets, stalled stages
 *   3. Momentum                20%  recent activity vs prior week
 *   4. Participant Readiness   10%  completed onboarding actions (AML, solicitors
 *                                    instructed, searches ordered, contract pack)
 *
 * The score adapts to the transaction structure automatically —
 * cash-buyer deals ignore mortgage milestones, chain-free deals
 * ignore chain nodes, freeholds ignore the Management Pack.
 *
 * Terminal short-circuit:
 *   • Exchanged / Completed → 100
 *   • Fallen Through        →   0
 *
 * Trend is computed by re-running the same algorithm with a
 * 7-day-earlier state (milestones completed >7d ago; activities
 * >7d ago) and comparing. NO snapshot table required.
 *
 * Everything the card renders comes back from the returned
 * `reasons[]` list so the score is fully explainable. Each reason
 * carries `{ label, tone, impact }` so the UI can rank + tint.
 *
 * Pure & deterministic. Same inputs → same outputs. Unit-tested.
 */

import { TERMINAL_STATUSES } from "../copy/dealRoom.js";
import { filterActiveBlockers } from "./useDealTransactionState.js";
import { nodeIdForStage } from "../data/transactionJourney.js";

/* ── Constants ─────────────────────────────────────────────── */
const WEIGHT_PROGRESS   = 40;
const WEIGHT_RISK       = 30;
const WEIGHT_MOMENTUM   = 20;
const WEIGHT_READINESS  = 10;

const TREND_THRESHOLD   = 2;   // points delta below this reads as "stable"

const MS_PER_DAY = 86400000;

/* ── Small utilities ───────────────────────────────────────── */
const _clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const _round = (v) => Math.round(v);

function _parseIso(v) {
  if (!v) return null;
  const t = typeof v === "number" ? v : Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

function _daysAgo(iso, now) {
  const t = _parseIso(iso);
  if (t == null) return null;
  return (now - t) / MS_PER_DAY;
}

function _isCompletedMilestone(m) {
  const s = String(m?.state || m?.status || "").toLowerCase();
  if (s === "done" || s === "complete" || s === "completed") return true;
  if (m?.completed === true) return true;
  if (m?.completed_at) return true;
  return false;
}

function _milestoneId(m) {
  return m?.step_id || m?.id || m?.key || null;
}

function _milestoneCompletedAt(m) {
  return m?.completed_at || m?.completedAt || m?.doneAt || m?.done_at || null;
}

function _hasOutstandingBlocker(deal, milestones, resolvedIds = null) {
  const dealBlocker = deal?.blocker || deal?.current_blocker;
  if (dealBlocker && String(dealBlocker).trim() && String(dealBlocker).trim().toLowerCase() !== "no blocker logged") {
    return true;
  }
  // iter-366 · `deal.blockers` (array) is the pilot / index shape;
  // iter-379 · Filtered through the shared stage-aware SSOT so
  // blockers whose stage is already behind the deal's current focus
  // (e.g. Mortgage Valuation Pending after Exchange) do NOT keep the
  // confidence score depressed.
  // P0 · 2026-08-12 — Also honour the Critical Path optimistic-
  // resolution sentinel so the confidence bump lands on the same
  // render as the count drop.
  const active = filterActiveBlockers(deal, { applicableNodes: [], currentNodeId: null, resolvedIds });
  for (const b of active) {
    if (b && (typeof b === "string" ? b.trim() : (b.title || b.copy || b.label || b.name))) return true;
  }
  for (const m of milestones || []) {
    if (m?.blocker && String(m.blocker).trim()) return true;
  }
  return false;
}

function _countActiveBlockers(deal, milestones, resolvedIds = null) {
  let count = 0;
  const dealBlocker = deal?.blocker || deal?.current_blocker;
  if (dealBlocker && String(dealBlocker).trim() && String(dealBlocker).trim().toLowerCase() !== "no blocker logged") {
    count += 1;
  }
  const active = filterActiveBlockers(deal, { applicableNodes: [], currentNodeId: null, resolvedIds });
  for (const b of active) {
    if (b && (typeof b === "string" ? b.trim() : (b.title || b.copy || b.label || b.name))) count += 1;
  }
  for (const m of milestones || []) {
    if (m?.blocker && String(m.blocker).trim()) count += 1;
  }
  return count;
}

/* ── Readiness detectors ───────────────────────────────────── */

/* A completed-action detector: was this readiness signal
 * observed in the current data? Each detector inspects the
 * milestones list + activity stream + participant roster and
 * returns `true` only when there's real evidence — never on
 * assigned-but-inactive roles. */
function _readinessSignals(deal, milestones, activities, participants, flags) {
  const done = new Set();
  for (const m of milestones || []) {
    if (_isCompletedMilestone(m)) {
      const id = _milestoneId(m);
      if (id) done.add(String(id).toLowerCase());
    }
  }

  const activityKinds = new Set();
  for (const a of activities || []) {
    const k = String(a?.kind || a?.activity_type || a?.type || "").toLowerCase();
    if (k) activityKinds.add(k);
  }

  const hasDoc = (label) => {
    const target = String(label || "").toLowerCase();
    for (const a of activities || []) {
      const name = String(a?.title || a?.name || a?.doc_name || a?.meta?.name || "").toLowerCase();
      if (name && name.includes(target)) return true;
    }
    return false;
  };

  const _participantByRole = (role) => {
    const target = String(role || "").toLowerCase().replace(/\s+/g, "_");
    return (participants || []).find((p) => {
      const r = String(p?.roleKey || p?.role_key || p?.participant_type || p?.role || "")
        .toLowerCase().replace(/\s+/g, "_");
      return r === target;
    });
  };

  // 1. AML complete
  const amlComplete = done.has("aml") || done.has("id_verification") || activityKinds.has("aml_verified");

  // 2. Solicitors instructed — must have BOTH sides + the instruction milestone / activity
  const bothSolicitors =
    !!_participantByRole("buyer_solicitor") && !!_participantByRole("seller_solicitor");
  const solicitorsInstructed = bothSolicitors && (
    done.has("solicitors")
    || done.has("instructed")
    || done.has("memo")
    || activityKinds.has("solicitor_instructed")
    || hasDoc("memorandum")
    || hasDoc("engagement")
  );

  // 3. Searches ordered
  const searchesOrdered =
    done.has("searches")
    || activityKinds.has("searches_ordered")
    || hasDoc("search");

  // 4. Contract Pack produced
  const contractPack =
    done.has("draft_contract")
    || done.has("contract_pack")
    || done.has("contracts")
    || hasDoc("contract pack")
    || hasDoc("draft contract");

  const rawSignals = [
    { key: "aml", ready: amlComplete, label: "AML complete" },
    { key: "solicitors", ready: solicitorsInstructed, label: "Solicitors instructed" },
    { key: "searches", ready: searchesOrdered, label: "Searches ordered" },
    { key: "contract_pack", ready: contractPack, label: "Contract pack produced" },
  ];

  // For cash-buyer deals we DON'T add mortgage — keep signal set stable at 4.
  // For chain-free we DON'T add chain — same. This keeps the readiness axis
  // fair across transaction types (never penalise a cash buyer for a
  // step that will never happen).
  return rawSignals;
}

/* ── Applicable milestones ─────────────────────────────────── */

/* Build the applicable step-id set for THIS deal. Given the pilot
 * canonical journey ids we handle the three canonical skip rules
 * from `resolveDealFlags`. Keep this local + defensive — never
 * throw on unexpected node shapes. */
function _defaultApplicableIds(flags) {
  // Canonical pilot journey ids. Aligned with journeyFor() in
  // `/app/frontend/src/data/transactionJourney.js`.
  const base = [
    "offer", "aml", "memo", "solicitors",
    "mortgage_app", "mortgage_offer",           // stripped for cash buyers
    "searches", "survey",
    "draft_contract", "mgmt_pack",              // mgmt pack leasehold-only
    "enquiries", "bsa", "signed_contract",
    "exchange", "completion",
  ];
  return base.filter((id) => {
    if (flags.skipMortgage && (id === "mortgage_app" || id === "mortgage_offer")) return false;
    if (flags.skipManagementPack && id === "mgmt_pack") return false;
    if (flags.skipChain && id === "bsa") return false;  // conservative
    return true;
  });
}

function _resolveApplicableIds(applicableNodes, flags) {
  if (Array.isArray(applicableNodes) && applicableNodes.length) {
    return applicableNodes
      .map((n) => (typeof n === "string" ? n : n?.id))
      .filter((id) => !!id)
      .map((id) => String(id).toLowerCase());
  }
  return _defaultApplicableIds(flags).map((id) => id.toLowerCase());
}

/* ── Core score derivation ─────────────────────────────────── */

function _scoreAt(deal, milestones, activities, participants, {
  applicableIds,
  flags,
  now,
  windowShiftDays = 0,  // 0 = current, 7 = one-week-ago snapshot
  /* P0 · 2026-08-12 — Optimistic resolution sentinel threaded from
   * the caller so both the current + snapshot score honour a
   * just-cleared blocker without waiting for the backend re-fetch. */
  resolvedIds = null,
}) {
  const cutoff = now - windowShiftDays * MS_PER_DAY;

  // ── Progress ──────────────────────────────────────────────
  const applicable = new Set(applicableIds);
  const applicableTotal = applicable.size;
  const completedById = new Map();  // id → completedAt (ms | null)
  for (const m of milestones || []) {
    if (!_isCompletedMilestone(m)) continue;
    const id = _milestoneId(m);
    if (!id) continue;
    const key = String(id).toLowerCase();
    if (!applicable.has(key)) continue;
    completedById.set(key, _parseIso(_milestoneCompletedAt(m)));
  }
  // iter-366 · `deal.completedSteps` is the index-side / pilot
  // shape (no per-milestone timestamps). Merge it in so progress
  // still reads correctly when only the deal is available.
  // iter-alignment · P0 · Journey SSOT Reconciliation
  // (2026-02-15) — the backend now populates canonical
  // `completed_steps` (snake_case) from milestone_completed
  // activity rows.  Both aliases fold into the progress
  // calculation.
  const _mergedCompletedSteps = [
    ...(Array.isArray(deal?.completedSteps) ? deal.completedSteps : []),
    ...(Array.isArray(deal?.completed_steps) ? deal.completed_steps : []),
  ];
  if (_mergedCompletedSteps.length > 0) {
    for (const id of _mergedCompletedSteps) {
      const key = String(id || "").toLowerCase();
      if (key && applicable.has(key) && !completedById.has(key)) {
        completedById.set(key, null);
      }
    }
  }
  let completedApplicable = 0;
  for (const [, cAt] of completedById) {
    if (windowShiftDays > 0 && cAt != null && cAt > cutoff) continue;
    completedApplicable += 1;
  }

  // iter-alignment · P40 — Stage-implied progress floor.
  // iter-alignment · P42.2 — Pilot calibration.
  //
  // The milestone SSOT is the primary progress signal, but pilot
  // deals routinely reach the operational Exchange / Completion
  // stage without every checklist row ticked (activity + journey
  // advance leaves some rows behind).  When that happens the
  // engine's raw `progressPct` under-counts, and Exchange
  // Confidence appears artificially low even though the deal has
  // materially advanced.
  //
  // Fix: floor `progressPct` by the deal's canonical stage
  // position — being AT node[i] means nodes[0..i-1] are complete
  // by definition of forward-only journey progression.  The floor
  // is derived from the SAME SSOT the rail uses (`nodeIdForStage`
  // + `applicableIds`), so there is no duplicate progression logic
  // and no separate scoring axis.
  //
  // Only applies when the caller supplied `applicableNodes` — i.e.
  // `applicableIds` are journey-node ids (mortgage / contract /
  // enquiries / exchange / complete).  Callers that fall back to
  // the finer-grained pilot id list are unaffected (their SSOT is
  // milestone-only by design).
  //
  // P42.2 calibration: the strict `stageIdx / total` floor treated
  // every un-started future stage as a NEGATIVE (0/N = 0 %) which
  // scored a healthy Offer Agreed deal at ~38 % / "At risk" even
  // when Memorandum was issued, no blockers, chain moving freely.
  // Future-stage milestones must contribute 0, not a negative.
  //
  // Re-derived floor: a linear ramp anchored at 0.5 for pre-
  // journey and 1.0 at the terminal Completion node.  All
  // deductions (blockers, overdue, inactivity, high-risk) still
  // subtract from the Risk axis unchanged — the recalibration
  // affects the Progress axis floor ONLY.
  //   • Offer Agreed  (idx 0 of 5)  → floor 0.50 → 20 pts of 40
  //   • Mortgage      (idx 0 of 5)  → floor 0.50
  //   • Contract      (idx 1 of 5)  → floor 0.625
  //   • Enquiries     (idx 2 of 5)  → floor 0.75
  //   • Exchange      (idx 3 of 5)  → floor 0.875
  //   • Complete      (idx 4 of 5)  → floor 1.00
  // Genuine milestone completions still push above the floor via
  // the `Math.max(...)` below.
  let stageImpliedPct = 0;
  const stageNodeId = nodeIdForStage(String(deal?.stage || "").toLowerCase());
  const stageIdx = stageNodeId ? applicableIds.indexOf(stageNodeId) : -1;
  const isRailShape = stageIdx >= 0 && applicableTotal > 0;
  if (isRailShape) {
    const denom = Math.max(1, applicableTotal - 1);
    stageImpliedPct = 0.5 + 0.5 * (stageIdx / denom);
  }
  const effectiveProgressPct = applicableTotal > 0
    ? Math.max(completedApplicable / applicableTotal, stageImpliedPct)
    : 0;
  const progressScore = WEIGHT_PROGRESS * effectiveProgressPct;
  // `effectiveCompleted` — display-only count used by the reasons
  // list.  Kept STRICT (real ticks + `stageIdx` prior nodes) so the
  // "X of Y milestones complete" copy stays truthful even when the
  // pilot-calibrated floor lifts the score above real completion.
  const effectiveCompleted = Math.max(
    completedApplicable,
    isRailShape ? stageIdx : 0,
  );

  // ── Risk & Blockers ───────────────────────────────────────
  let riskScore = WEIGHT_RISK;
  const blockerCount = _countActiveBlockers(deal, milestones, resolvedIds);
  riskScore -= Math.min(blockerCount * 8, 24);

  const highRisk = String(deal?.risk || "").toLowerCase() === "high";
  if (highRisk) riskScore -= 8;

  const daysInStage = Number(deal?.days_in_stage || deal?.daysInStage || 0);
  if (daysInStage > 14) riskScore -= 6;
  else if (daysInStage > 21) riskScore -= 10;

  const targetX = _parseIso(deal?.target_exchange || deal?.targetExchange || deal?.exchangeDate);
  if (targetX != null && targetX < cutoff) {
    // Overdue exchange target relative to the comparison cutoff.
    riskScore -= 10;
  }

  // Silence: no activity for >14 days is a warning signal.
  let latestActivity = 0;
  for (const a of activities || []) {
    const t = _parseIso(a?.at || a?.created_at || a?.createdAt || a?.timestamp);
    if (t != null && t <= cutoff && t > latestActivity) latestActivity = t;
  }
  if (latestActivity > 0 && (cutoff - latestActivity) / MS_PER_DAY > 14) {
    riskScore -= 5;
  }

  riskScore = _clamp(riskScore, 0, WEIGHT_RISK);

  // ── Momentum ──────────────────────────────────────────────
  // recent = last 7d, prior = 8–14d, both relative to the cutoff.
  let recent = 0;
  let prior = 0;
  for (const a of activities || []) {
    const t = _parseIso(a?.at || a?.created_at || a?.createdAt || a?.timestamp);
    if (t == null || t > cutoff) continue;
    const daysBack = (cutoff - t) / MS_PER_DAY;
    if (daysBack <= 7) recent += 1;
    else if (daysBack <= 14) prior += 1;
  }
  // Also count recently-completed milestones as momentum evidence
  // — a milestone completion is a first-class operational event.
  for (const m of milestones || []) {
    const t = _parseIso(_milestoneCompletedAt(m));
    if (t == null || t > cutoff) continue;
    const daysBack = (cutoff - t) / MS_PER_DAY;
    if (daysBack <= 7) recent += 1;
    else if (daysBack <= 14) prior += 1;
  }
  let momentumScore = 0;
  if (recent === 0 && prior === 0) {
    momentumScore = 0;
  } else if (recent >= prior) {
    // Scales up to full weight at ≥5 events / week.
    momentumScore = WEIGHT_MOMENTUM * Math.min(1, recent / 5);
  } else {
    // Softening — cap at half weight.
    momentumScore = (WEIGHT_MOMENTUM / 2) * Math.min(1, recent / Math.max(prior, 1));
  }
  momentumScore = _clamp(momentumScore, 0, WEIGHT_MOMENTUM);

  // ── Readiness ─────────────────────────────────────────────
  const signals = _readinessSignals(deal, milestones, activities, participants, flags);
  // For the prior snapshot, restrict "ready" to signals whose
  // underlying milestone was completed before the cutoff (best
  // effort — activity-only signals count if the activity is old
  // enough).
  const readySignals = signals.filter((s) => {
    if (!s.ready) return false;
    if (windowShiftDays === 0) return true;
    // Find the underlying evidence's timestamp. Fall back to
    // "counts unless we can prove it's recent".
    const key = s.key;
    for (const m of milestones || []) {
      if (!_isCompletedMilestone(m)) continue;
      const id = String(_milestoneId(m) || "").toLowerCase();
      if (id.includes(key) || (key === "aml" && id === "aml") || (key === "contract_pack" && id.includes("contract"))) {
        const t = _parseIso(_milestoneCompletedAt(m));
        if (t != null && t > cutoff) return false;
      }
    }
    return true;
  });
  const readinessPct = signals.length > 0 ? readySignals.length / signals.length : 0;
  const readinessScore = WEIGHT_READINESS * readinessPct;

  // ── Compose ───────────────────────────────────────────────
  const total = _clamp(progressScore + riskScore + momentumScore + readinessScore, 0, 100);

  return {
    total,
    factors: {
      progress:  { score: progressScore,  weight: WEIGHT_PROGRESS,  completed: effectiveCompleted, total: applicableTotal },
      risk:      { score: riskScore,      weight: WEIGHT_RISK,      blockers: blockerCount, highRisk, daysInStage },
      momentum:  { score: momentumScore,  weight: WEIGHT_MOMENTUM,  recent, prior },
      readiness: { score: readinessScore, weight: WEIGHT_READINESS, signals, readySignals },
    },
  };
}

/* ── Reason ranking ────────────────────────────────────────── */

function _reasonsFor(current, prev) {
  const reasons = [];
  const p = current.factors.progress;
  const r = current.factors.risk;
  const m = current.factors.momentum;
  const rd = current.factors.readiness;

  // Progress
  if (p.total > 0) {
    reasons.push({
      key: "progress",
      label: `${p.completed} of ${p.total} milestones complete`,
      impact: p.score,
      tone: p.completed >= p.total * 0.7 ? "positive" : p.completed >= p.total * 0.4 ? "neutral" : "watch",
    });
  }

  // Risk / blockers
  if (r.blockers > 0) {
    reasons.push({
      key: "blockers",
      label: `${r.blockers} ${r.blockers === 1 ? "blocker" : "blockers"} outstanding`,
      impact: -(r.blockers * 8),
      tone: r.blockers >= 3 ? "risk" : "watch",
    });
  }
  if (r.daysInStage > 14) {
    reasons.push({
      key: "stalled",
      label: `Stalled ${r.daysInStage}d in current stage`,
      impact: -6,
      tone: "watch",
    });
  }
  if (r.highRisk) {
    reasons.push({
      key: "high_risk",
      label: "Deal flagged as high risk",
      impact: -8,
      tone: "risk",
    });
  }

  // Momentum
  if (m.recent > 0 || m.prior > 0) {
    if (m.recent >= m.prior && m.recent >= 3) {
      reasons.push({
        key: "momentum_up",
        label: `Strong momentum · ${m.recent} events in last 7d`,
        impact: m.score,
        tone: "positive",
      });
    } else if (m.recent < m.prior) {
      reasons.push({
        key: "momentum_down",
        label: `Momentum softening · ${m.recent} events last 7d vs ${m.prior} prior`,
        impact: -(WEIGHT_MOMENTUM - m.score),
        tone: "watch",
      });
    }
  } else {
    reasons.push({
      key: "no_activity",
      label: "No recent operational activity",
      impact: -WEIGHT_MOMENTUM,
      tone: "watch",
    });
  }

  // Readiness
  const readyCount = rd.readySignals.length;
  const totalCount = rd.signals.length;
  if (totalCount > 0 && readyCount < totalCount) {
    const missing = rd.signals.filter((s) => !s.ready).map((s) => s.label);
    reasons.push({
      key: "readiness_gap",
      label: `Readiness · ${readyCount} of ${totalCount} · missing ${missing[0]}${missing.length > 1 ? ` +${missing.length - 1}` : ""}`,
      impact: -(WEIGHT_READINESS - rd.score),
      tone: readyCount === 0 ? "watch" : "neutral",
    });
  } else if (totalCount > 0 && readyCount === totalCount) {
    reasons.push({
      key: "readiness_full",
      label: "All readiness signals met",
      impact: WEIGHT_READINESS,
      tone: "positive",
    });
  }

  // Rank by absolute impact and return the top 3.
  return reasons
    .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
    .slice(0, 3);
}

/* ── Public entry point ────────────────────────────────────── */

/**
 * Compute the dynamic Exchange Confidence for a deal.
 *
 * @param {object} deal        The current deal record.
 * @param {object} opts
 * @param {Array}  opts.milestones   Milestone rows (state / completed_at / blocker).
 * @param {Array}  opts.activities   Activity rows (kind / at).
 * @param {Array}  opts.participants Enriched participants (roleKey).
 * @param {Array}  opts.applicableNodes  Applicable journey nodes (from SSOT).
 * @param {object} opts.flags        `resolveDealFlags(deal)` output (from SSOT).
 * @param {number} opts.now          Injected clock (ms since epoch). Unit-testable.
 *
 * @returns {{
 *   score: number,                       // 0–100
 *   trend: "improving"|"stable"|"softening",
 *   trendDelta: number,                  // signed points delta vs 7d ago
 *   milestonesDone: number,
 *   milestonesTotal: number,
 *   activeBlockers: number,
 *   reasons: Array<{key,label,tone,impact}>,
 *   factors: object,                     // full breakdown for explainability
 *   terminal: null|"Exchanged"|"Completed"|"Fallen Through",
 * }}
 */
export function computeExchangeConfidence(deal, opts = {}) {
  const {
    milestones = [],
    activities = [],
    participants = [],
    applicableNodes = null,
    flags = { skipMortgage: false, skipChain: false, skipManagementPack: false },
    now = Date.now(),
    /* P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12) —
     * Client-side sentinel of blocker ids just resolved via the
     * Critical Path card.  Threaded into `_countActiveBlockers`
     * and `_hasOutstandingBlocker` so the confidence score
     * updates on the same render pass as the count / banner /
     * NBA — no more "resolved but score didn't move" gap. */
    resolvedIds = null,
  } = opts;

  const safeDeal = deal || {};
  const status = String(safeDeal.status || "");
  const stage = String(safeDeal.stage || "").toLowerCase();

  // ── Terminal short-circuit ────────────────────────────────
  // Both `status` (title-case: Exchanged / Completed) and `stage`
  // (lowercase: exchanged / completed) are SSOT surfaces on the
  // deal record.  Either being terminal is enough — this makes the
  // engine robust to any future path that advances stage without
  // reconciling status (the backend enforces the invariant at the
  // write boundary via `deal_status_ssot.reconcile_deal_status`,
  // but reading defensively costs nothing).
  const terminalFromStage = stage === "exchanged" ? "Exchanged"
    : stage === "completed" ? "Completed"
    : null;
  const terminalStatus = TERMINAL_STATUSES.has(status)
    ? status
    : terminalFromStage;

  if (terminalStatus) {
    if (terminalStatus === "Completed") {
      // Completed is a true terminal state — nothing left to
      // compute.  Score reads 100 as a coherent "done" pill.
      return {
        score: 100,
        trend: "stable",
        trendDelta: 0,
        milestonesDone: 0,
        milestonesTotal: 0,
        activeBlockers: 0,
        reasons: [{
          key: "terminal_success",
          label: "Deal completed",
          tone: "positive",
          impact: 100,
        }],
        factors: null,
        terminal: terminalStatus,
      };
    }
    if (terminalStatus === "Exchanged") {
      /* P0 · Property Type + Readiness Lifecycle (2026-02-28)
       * ---------------------------------------------------------
       * Post-exchange Completion Readiness — computed from real
       * deal signals instead of the previous hard-coded 100.
       *
       * Inputs (all already on the deal envelope):
       *   • `target_completion` / `completion_date` — the
       *     scheduled completion ISO.
       *   • `now` — injected clock for testability.
       *   • Days until (or overdue by) completion.
       *
       * Curve (calm, additive):
       *   base 100 → deduct on missing/overdue completion date.
       *
       * The score stays high on healthy post-exchange deals
       * (drawdown pending, keys logistics booked) and drops
       * meaningfully when the completion is materially slipping.
       * Never rewards imaginary work — every deduction points to
       * a real, operator-actionable signal. */
      const completionIso =
        safeDeal.target_completion ||
        safeDeal.targetCompletion ||
        safeDeal.completion_date ||
        safeDeal.completionDate ||
        null;
      const completionMs = completionIso ? Date.parse(completionIso) : null;
      const daysToCompletion =
        (completionMs !== null && !Number.isNaN(completionMs))
          ? Math.round((completionMs - now) / MS_PER_DAY)
          : null;

      let score = 100;
      const reasons = [{
        key: "exchange_done",
        label: "Contracts exchanged",
        tone: "positive",
        impact: 60,
      }];

      if (completionIso === null) {
        score -= 30;
        reasons.push({
          key: "completion_missing",
          label: "Completion date not set",
          tone: "watch",
          impact: -30,
        });
      } else if (daysToCompletion !== null && daysToCompletion < -14) {
        score -= 55;
        reasons.push({
          key: "completion_very_overdue",
          label: `Completion overdue by ${Math.abs(daysToCompletion)} days`,
          tone: "risk",
          impact: -55,
        });
      } else if (daysToCompletion !== null && daysToCompletion < -3) {
        score -= 30;
        reasons.push({
          key: "completion_overdue",
          label: `Completion overdue by ${Math.abs(daysToCompletion)} days`,
          tone: "risk",
          impact: -30,
        });
      } else if (daysToCompletion !== null && daysToCompletion <= 7) {
        reasons.push({
          key: "completion_imminent",
          label: daysToCompletion <= 0
            ? "Completion today"
            : `Completion in ${daysToCompletion} day${daysToCompletion === 1 ? "" : "s"}`,
          tone: "positive",
          impact: 20,
        });
      } else if (daysToCompletion !== null && daysToCompletion <= 30) {
        reasons.push({
          key: "completion_scheduled",
          label: `Completion in ${daysToCompletion} days`,
          tone: "positive",
          impact: 10,
        });
      } else if (daysToCompletion !== null) {
        reasons.push({
          key: "completion_long_horizon",
          label: `Completion in ${daysToCompletion} days`,
          tone: "neutral",
          impact: 0,
        });
      }

      // Any active blockers still tagged post-exchange (keys /
      // drawdown / removals) subtract calmly.  Empty array is
      // the common case — the pre-exchange catalogue does not
      // fire for stage='exchanged' — so this branch is a no-op
      // on today's data but stays wired for future post-exchange
      // detectors.
      // P0 · 2026-08-12 — Honour the optimistic-resolution
      // sentinel so post-exchange resolutions cascade too.
      const blockerArr = Array.isArray(safeDeal.blockers) ? safeDeal.blockers : [];
      const hasResolved = resolvedIds && typeof resolvedIds.has === "function";
      const activeBlockersCount = blockerArr.filter((b) => {
        if (!b) return false;
        const s = String(b.status || "").toLowerCase();
        if (s === "resolved" || s === "cleared") return false;
        if (hasResolved) {
          const bid = String(b.id || b.step_id || b.title || "");
          if (bid && resolvedIds.has(bid)) return false;
        }
        return true;
      }).length;
      if (activeBlockersCount > 0) {
        const impact = -15 * activeBlockersCount;
        score += impact;
        reasons.push({
          key: "post_exchange_blockers",
          label: `${activeBlockersCount} outstanding item${activeBlockersCount === 1 ? "" : "s"} before completion`,
          tone: "risk",
          impact,
        });
      }

      score = Math.max(0, Math.min(100, Math.round(score)));

      return {
        score,
        trend: "stable",
        trendDelta: 0,
        milestonesDone: 0,
        milestonesTotal: 0,
        activeBlockers: activeBlockersCount,
        reasons: reasons.slice(0, 3),
        factors: null,
        terminal: terminalStatus,
      };
    }
    // Fallen Through / Archived
    return {
      score: 0,
      trend: "stable",
      trendDelta: 0,
      milestonesDone: 0,
      milestonesTotal: 0,
      activeBlockers: 0,
      reasons: [{
        key: "terminal_failure",
        label: "Deal fell through",
        tone: "risk",
        impact: -100,
      }],
      factors: null,
      terminal: terminalStatus,
    };
  }

  const applicableIds = _resolveApplicableIds(applicableNodes, flags);
  // Cash purchase: leftover Mortgage milestones / events are not on the rail and never count.
  const cash = safeDeal.is_cash_buyer === true || safeDeal.isCashBuyer === true || !!flags?.skipMortgage;
  const mIds = new Set((milestones || []).filter((m) => m?.stage === "mortgage").map((m) => m.id));
  const ms = cash ? (milestones || []).filter((m) => m?.stage !== "mortgage") : milestones;
  const acts = cash ? (activities || []).filter((a) => {
    const meta = a?.meta || {};
    return !(meta.journey_step === "mortgage" || mIds.has(meta.milestone_id) || mIds.has(meta.via_milestone)
      || String(a?.activity_type || "").startsWith("mortgage_"));
  }) : activities;
  const current = _scoreAt(safeDeal, ms, acts, participants, {
    applicableIds, flags, now, windowShiftDays: 0, resolvedIds,
  });
  const prior = _scoreAt(safeDeal, ms, acts, participants, {
    applicableIds, flags, now, windowShiftDays: 7, resolvedIds,
  });

  const score = _round(current.total);
  const priorScore = _round(prior.total);
  const trendDelta = score - priorScore;

  let trend = "stable";
  if (trendDelta >= TREND_THRESHOLD) trend = "improving";
  else if (trendDelta <= -TREND_THRESHOLD) trend = "softening";

  const reasons = _reasonsFor(current, prior);

  return {
    score,
    trend,
    trendDelta,
    milestonesDone: current.factors.progress.completed,
    milestonesTotal: current.factors.progress.total,
    activeBlockers: current.factors.risk.blockers,
    reasons,
    factors: current.factors,
    terminal: null,
  };
}

/* Named exports for weights + testing hooks. */
export const CONFIDENCE_WEIGHTS = {
  progress: WEIGHT_PROGRESS,
  risk: WEIGHT_RISK,
  momentum: WEIGHT_MOMENTUM,
  readiness: WEIGHT_READINESS,
};
